/*
 * ss-wear-it-with.js — <wear-it-with> (sections/ss-wear-it-with.liquid) and
 * its quick-shop sheet <wear-it-with-quick-view>.
 *
 * Looks: the current product (hero tile, server-rendered in the section's
 * <template>) + two Rebuy picks per look from the section's data source
 * (GET rebuyengine.com/api/v1/custom/id/<id>?shopify_product_ids=<pid>).
 * The data source's matched rules list their output collections; each look
 * takes one product per output collection, in Rebuy's rank order (a tee gets
 * bottoms + footwear, not two pairs of shoes), colourways deduped by title.
 * Falls back to grouping by product type when the rules give fewer than two
 * collections with products. Results cache per product in sessionStorage.
 *
 * Quick view: fetches /products/<handle>?section_id=ss-wear-it-with-quick-view
 * (sections/ss-wear-it-with-quick-view.liquid) and injects its .wiw-qv__content.
 * Size taps swap the price row / ADD TO CART from the buttons' data-*; adding
 * posts /cart/add.js with the drawer's sections (same contract as
 * product-form.js) and hands the response to cart-drawer.renderContents.
 */

(() => {
  const STORAGE_PREFIX = 'santo-wiw';
  // bump when the pairing/ranking logic changes so sessions don't keep
  // showing looks built by the previous version for up to CACHE_TTL_MS
  const CACHE_VERSION = 22; // 22: athlete looks applied per page, cache holds Rebuy looks only
  const CACHE_TTL_MS = 30 * 60 * 1000;
  // Shopify's own best-selling order (templates/collection.wiw-rank.liquid),
  // shared by every product page for the session.
  const RANK_CACHE_KEY = `${STORAGE_PREFIX}:rank:v2`;
  // look 1 is drawn from each slot's top sellers, rotated per product so
  // neighbouring products don't all open on the same #1
  const TOP_SELLERS = 9;
  const REBUY_ENDPOINT = 'https://rebuyengine.com/api/v1/custom/id/';
  const REBUY_FIELDS = [
    'id', 'title', 'handle', 'product_type', 'image', 'collection_ids', 'options', 'status', 'published_at',
    'variants.id', 'variants.option1', 'variants.option2', 'variants.option3',
    'variants.inventory_quantity', 'variants.inventory_management', 'variants.inventory_policy',
    'variants.price', 'variants.compare_at_price',
  ].join(',');
  const REBUY_TOP_SELLERS = 'https://rebuyengine.com/api/v1/products/top_sellers';
  // Section setting "Rank picks by": sales | full_price_first | newest | price
  const DEFAULT_RANK_MODE = 'newest';
  // "newest": published within this many days forms the first tier
  const NEW_DAYS = 90;
  // the render waits this long for the fast (Shopify) rank, then goes without
  const RANK_BUDGET_MS = 2500;
  const withTimeout = (promise, ms) =>
    new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('timeout')), ms);
      promise.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
    });
  const fetchSalesRankWarm = { started: false };

  const escapeHtml = (str) =>
    String(str ?? '').replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );

  // Rebuy hands back the full-resolution original; ask the Shopify CDN for a
  // tile-sized render instead (tiles are ~180px wide, 600 covers 3x).
  const sizedImage = (src, width) => {
    if (!src) return '';
    const s = String(src).trim();
    if (!/^(https?:)?\/\//i.test(s)) return '';
    if (/cdn\.shopify\.com/.test(s) && !/[?&]width=/i.test(s)) {
      return s + (s.includes('?') ? '&' : '?') + 'width=' + width;
    }
    return s;
  };

  const variantAvailable = (v) => {
    if (!v) return false;
    if (!v.inventory_management) return true;
    if (v.inventory_quantity > 0) return true;
    return v.inventory_policy === 'continue';
  };

  // Stock depth: a product with one or two sizes left (a tee down to S, a pant
  // down to 30x30) is a dead end for most shoppers and reads as "why is this
  // here". A pick needs at least MIN_SIZES purchasable sizes, one of them a
  // core size: M/L for alpha sizing, waist 32/34 for numeric (also "32x32"),
  // 10/11 for footwear. Products without a Size option (one-size) pass.
  const MIN_SIZES = 3;
  // a colour family needs this many candidates in a slot to get its own look
  const MIN_FAMILY_POOL = 3;
  const CORE_SIZES = new Set(['m', 'l', '32', '34', '10', '11']);
  const sizeOf = (p, v) => {
    const names = (p?.options || []).map((o) => String(o?.name || o || '').toLowerCase());
    const i = names.indexOf('size');
    const raw = i >= 0 ? v?.[`option${i + 1}`] : v?.option2 ?? v?.option1;
    return String(raw ?? '').trim().toLowerCase();
  };
  const isCoreSize = (size) => {
    const base = size.split(/[x/ ]/)[0].replace(/\.0$/, ''); // "32x32" -> "32", "10.0" -> "10"
    return CORE_SIZES.has(base);
  };
  const wellStocked = (p) => {
    const names = (p?.options || []).map((o) => String(o?.name || o || '').toLowerCase());
    if (!names.includes('size')) return true;
    const sizes = (p?.variants || []).filter(variantAvailable).map((v) => sizeOf(p, v)).filter(Boolean);
    return sizes.length >= MIN_SIZES && sizes.some(isCoreSize);
  };

  const maxPrice = (p) => Math.max(0, ...(p?.variants || []).map((v) => parseFloat(v?.price) || 0));
  const publishedMs = (p) => {
    const t = Date.parse(p?.published_at || p?.created_at || '');
    return Number.isFinite(t) ? t : 0;
  };
  const isNew = (p) => publishedMs(p) > Date.now() - NEW_DAYS * 86400000;
  // first-tier test per rank mode (drives both ordering and the rotation window)
  const inFirstTier = (p, mode) => (mode === 'full_price_first' ? !isMarkedDown(p) : mode === 'newest' ? isNew(p) : true);
  // marked down = any variant's compare-at above its price
  const isMarkedDown = (p) =>
    (p?.variants || []).some((v) => (parseFloat(v?.compare_at_price) || 0) > (parseFloat(v?.price) || 0));

  // "Matching" first: within each output collection, a candidate from the
  // same line or collab as the current product (shared title words, e.g.
  // "Tempo Tech", "Jett Lawrence") ranks ahead of Rebuy's default order, and
  // among those the same colourway wins. Candidates with no shared words —
  // the shoes next to a tee — keep Rebuy's ranking untouched, so a black tee
  // doesn't end up with an all-black wardrobe.
  const GARMENT_WORDS = new Set([
    'tee', 'tees', 't-shirt', 'tshirt', 'shirt', 'short', 'shorts', 'pant', 'pants', 'hoodie', 'hoodies',
    'jacket', 'jackets', 'tank', 'top', 'tops', 'polo', 'sneaker', 'sneakers', 'mule', 'mules', 'boot', 'boots',
    'sweater', 'sweatshirt', 'crewneck', 'jersey', 'denim', 'jean', 'jeans', 'cap', 'hat', 'sock', 'socks',
    'lined', 'liner', 'the', 'and', 'with', 'of', 'in', 'a', 'an', 'edition',
  ]);
  const titleTokens = (title) =>
    new Set(
      String(title || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length > 1 && !GARMENT_WORDS.has(w) && !/^\d+$/.test(w)),
    );
  const colourOf = (p) => {
    const options = Array.isArray(p?.options) ? p.options : [];
    let idx = options.findIndex((o) => /colou?r/i.test(typeof o === 'string' ? o : o?.name || ''));
    if (idx === -1) idx = 0;
    const v = p?.variants?.[0];
    return String(v?.[`option${idx + 1}`] || '').trim().toLowerCase();
  };
  const similarity = (current, candidate) => {
    const a = titleTokens(current?.title);
    let shared = 0;
    let extra = 0;
    for (const w of titleTokens(candidate?.title)) {
      if (a.has(w)) shared += 1;
      else extra += 1;
    }
    if (!shared) return 0;
    // the plain "Tempo Tech Tee" beats "Tempo 777 Tech Tee - Ryan Sheckler
    // Edition" next to a plain Tempo Tech Short; same colourway edges it
    const colour = colourOf(current);
    const sameColour = colour && colour === colourOf(candidate) ? 0.5 : 0;
    return shared - 0.5 * extra + sameColour;
  };

  // Colour families for coherent looks: order matters ("Black Fade" is black,
  // "Fade Blue" is blue, "Carrara Grey" is grey). Neutrals go with anything.
  const COLOUR_FAMILIES = [
    ['black', /black|onyx/],
    ['white', /white/],
    ['cream', /cream|ivory|bone|ecru|natural|oat/],
    ['grey', /gr[ae]y|charcoal|heather|slate|silver/],
    ['blue', /blue|navy|indigo|denim|aqua|teal/],
    ['red', /red|merlot|wine|burgundy|rose|pink|magenta|maroon|cherry/],
    ['green', /green|olive|army|forest|sage/],
    ['yellow', /yellow|gold|mustard/],
    ['purple', /purple|lavender|lilac/],
    ['brown', /brown|espresso|chocolate|cheetah|camo|mocha|coffee/],
    ['tan', /tan|beige|sand|khaki|camel|stone/],
  ];
  const NEUTRAL_FAMILIES = new Set(['black', 'white', 'cream', 'grey', 'tan']);
  const familyOf = (colour) => {
    const c = String(colour || '').toLowerCase();
    if (!c) return '';
    for (const [name, re] of COLOUR_FAMILIES) if (re.test(c)) return name;
    return 'other';
  };

  // Garment category from product_type (title as a fallback). Used to place
  // candidates whose Rebuy copy doesn't list the rule's output collection yet
  // (Rebuy's catalogue lags new collections) into the right tile slot.
  const CATEGORIES = [
    ['footwear', /shoe|sneaker|mule|footwear|boot|slide|sandal|trainer/],
    ['bottoms', /pant|short|denim|jean|trouser|jogger|sweatpant|chino/],
    ['outer', /jacket|coat|zip|hood|crewneck|sweatshirt|fleece|pullover|windbreaker|vest|anorak/],
    ['tops', /tee|t-shirt|shirt|top|tank|polo|knit|sweater|jersey|sleeve|blouse/],
    ['accessories', /hat|cap|bag|sock|belt|accessor|beanie|wallet/],
  ];
  const categoryOf = (p) => {
    for (const text of [p?.product_type, p?.title]) {
      const t = String(text || '').toLowerCase();
      if (!t) continue;
      for (const [name, re] of CATEGORIES) if (re.test(t)) return name;
    }
    return 'other';
  };

  const LONG_BOTTOMS = /pant|denim|jean|trouser|jogger|sweatpant|chino pant/;
  const LONG_SLEEVE_PRODUCT = /long sleeve|\bls\b|longsleeve|knit|sweater|hood|fleece|crew|sweatshirt|jacket|pant|denim/;
  const isLongBottom = (p) => LONG_BOTTOMS.test(String(p?.product_type || '').toLowerCase()) || LONG_BOTTOMS.test(String(p?.title || '').toLowerCase());
  const wantsLongBottoms = (current) =>
    LONG_SLEEVE_PRODUCT.test(String(current?.title || '').toLowerCase()) ||
    LONG_SLEEVE_PRODUCT.test(String(current?.product_type || '').toLowerCase());

  // round-robin merge keeps each source's own (Rebuy) order while alternating
  const interleave = (lists) => {
    const out = [];
    const max = Math.max(0, ...lists.map((l) => l.length));
    for (let i = 0; i < max; i += 1) for (const l of lists) if (i < l.length) out.push(l[i]);
    return out;
  };

  // the garment kind most of a slot's candidates share
  const dominantOf = (g) => {
    const n = {};
    for (const p of g) n[categoryOf(p)] = (n[categoryOf(p)] || 0) + 1;
    return Object.keys(n).sort((a, b) => n[b] - n[a])[0] || 'other';
  };

  // Tiles read head to toe, left to right: tops (the base layer leads, the
  // jacket over it comes next), bottoms, footwear. Without this the slots sat
  // in whatever order the matched rule listed its output collections, which
  // differs per rule — pants left of the tee on one product, right of it on
  // the next. Stable: ties keep the rules' order.
  const WEAR_ORDER = ['tops', 'outer', 'bottoms', 'footwear', 'accessories'];
  const wearRank = (g) => {
    const i = WEAR_ORDER.indexOf(dominantOf(g));
    return i < 0 ? WEAR_ORDER.length : i;
  };
  const sortSlotsByWear = (groups) =>
    groups
      .map((g, i) => ({ g, i, r: wearRank(g) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.g);

  const mergeSlotsByCategory = (groups, current) => {
    const byCat = new Map();
    for (const g of groups) {
      const cat = dominantOf(g);
      const key = cat === 'other' ? `other-${byCat.size}` : cat;
      if (!byCat.has(key)) byCat.set(key, []);
      byCat.get(key).push(g);
    }
    const merged = [];
    for (const [key, lists] of byCat) {
      if (lists.length === 1) {
        merged.push(lists[0]);
        continue;
      }
      let slot = interleave(lists);
      if (key === 'bottoms' && wantsLongBottoms(current)) {
        // stable partition: long bottoms first, each side keeping its order
        slot = slot.filter(isLongBottom).concat(slot.filter((p) => !isLongBottom(p)));
      }
      merged.push(slot);
    }
    return merged;
  };

  // Per-product rotation so neighbouring products don't all surface the same
  // neutral pieces: a stable hash of the product id picks where in Rebuy's
  // ranked list each neutral look starts.
  const hashString = (str) => {
    let h = 5381;
    for (const ch of String(str || '')) h = ((h * 33) ^ ch.charCodeAt(0)) >>> 0;
    return h;
  };
  // Product ids are near-identical long numbers, so the raw hash clusters:
  // six Trento colourways landed on the same two tees. Mix the bits before
  // using the seed so neighbouring ids spread across the window.
  const mix = (h) => {
    let x = h >>> 0;
    x ^= x >>> 16;
    x = Math.imul(x, 0x45d9f3b) >>> 0;
    x ^= x >>> 16;
    x = Math.imul(x, 0x45d9f3b) >>> 0;
    x ^= x >>> 16;
    return x >>> 0;
  };
  const rotate = (arr, k) => {
    if (!arr.length) return arr;
    const n = k % arr.length;
    return arr.slice(n).concat(arr.slice(0, n));
  };
  // rotate only the first `top` entries (the best sellers), keep the rest ranked
  const rotateTop = (arr, top, k) => rotate(arr.slice(0, top), k).concat(arr.slice(top));
  // Rotation window per slot. In full-price-first mode it is the full-price
  // tier itself: never past it (with five full-price pants and a window of
  // nine, a product whose rotation started at position six opened on three
  // markdowns in a row) and never narrower than it (a fixed nine over ~20
  // full-price tees put the same tee on 4 of 6 Trentos — every tee in that
  // tier is a $60 full-price graphic tee, so variety beats strict rank).
  // Other modes rotate over the top half of the slot, at least `top`.
  const tierWidth = (arr, mode, top) => {
    if (mode !== 'full_price_first' && mode !== 'newest') return Math.max(top, Math.floor(arr.length / 2));
    let n = 0;
    while (n < arr.length && inFirstTier(arr[n], mode)) n += 1;
    return n >= 2 ? n : Math.max(top, Math.floor(arr.length / 2));
  };

  const storefrontRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  };

  class WearItWith extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;
      this.track = this.querySelector('[data-wiw-track]');
      this.template = this.querySelector('[data-wiw-look-template]');
      this.counter = this.querySelector('[data-wiw-counter]');
      this.fill = this.querySelector('[data-wiw-progress]');
      this.quickView = this.querySelector('wear-it-with-quick-view');
      this.productId = String(this.dataset.productId || '');
      this.maxLooks = Math.max(1, parseInt(this.dataset.maxLooks, 10) || 3);
      if (!this.track || !this.template || !this.productId) return;
      this.addEventListener('click', this.onClick.bind(this));
      this.load();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    async load() {
      let looks = this.readCache();
      if (!looks) {
        looks = await this.fetchLooks();
        if (looks) this.writeCache(looks);
      }
      // Athlete looks are built here, per page, from THIS product's athlete
      // collection — never cached: the sibling prefetch below shares a cache
      // with products that are not this athlete's (the Trento siblings span
      // every Trento edition), and caching them once put Twitch's gear on the
      // Cole and base Trentos.
      looks = await this.withAthleteLook(looks || [], this.productId);
      if (!looks || !looks.length) {
        this.hidden = true;
        return;
      }
      this.render(looks);
    }

    // ----- Athlete bundle -------------------------------------------------
    // When the product belongs to an athlete's collection (data-athlete-
    // collection, picked server-side), look 1 is built from THAT collection:
    // the athlete's own tee / short / shoe around the product. Rebuy's rules
    // return the lifestyle pools, which rarely carry the athlete's pieces (the
    // Twitch Trento's pool had 0 of 88), so this is a separate storefront
    // fetch. Both tiles must come from the collection or there is no bundle;
    // the Rebuy looks follow, minus any product the bundle already shows.

    athleteProducts() {
      const handle = (this.dataset.athleteCollection || '').trim();
      if (!handle) return Promise.resolve([]);
      if (!this.__athlete) {
        this.__athlete = fetch(`${storefrontRoot()}collections/${encodeURIComponent(handle)}/products.json?limit=250`, {
          credentials: 'same-origin',
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((json) => (json && Array.isArray(json.products) ? json.products : []))
          .catch(() => []);
      }
      return this.__athlete;
    }

    // Curated fits from the Farm to Table entry (fit_1..fit_3), serialised by
    // the section. These are the preferred source: hand-picked, no rules.
    curatedFits() {
      try {
        const fits = JSON.parse(this.dataset.athleteFits || '[]');
        return Array.isArray(fits) ? fits.filter((fit) => Array.isArray(fit) && fit.length === 2).map((fit) => fit.map((p) => ({ ...p, id: String(p.id) }))) : [];
      } catch {
        return [];
      }
    }

    async withAthleteLook(looks, productId) {
      const base = Array.isArray(looks) ? looks : [];
      // Nth view of this product in the session: drives which look opens the
      // carousel and which pair of glasses fills in, so each reload moves on
      let view = 0;
      try {
        const key = `${STORAGE_PREFIX}:view:${productId}`;
        view = (parseInt(sessionStorage.getItem(key), 10) || 0) + 1;
        sessionStorage.setItem(key, String(view));
      } catch {
        view = Math.floor(Math.random() * 1000);
      }

      // Curated fits win outright: an explicit selection shows even where no
      // Rebuy rule covers the product. Rebuy looks fill any remaining slots.
      const curated = this.curatedFits();
      if (curated.length) {
        const shown = new Set(curated.flat().map((p) => p.id));
        const rest = base.filter((look) => !look.some((p) => shown.has(String(p.id))));
        const all = [...curated, ...rest].slice(0, this.maxLooks);
        return rotate(all, view % all.length);
      }

      // ----- Automatic athlete looks (fallback until fits are curated) -----
      // They only reshape a carousel that exists: the section shows where a
      // Rebuy rule selects the product, and nowhere else (not on sunglasses
      // or on pieces no rule covers).
      if (!base.length) return base;
      // The athlete's name, from the collection title ("Twitch x Santo
      // Studio" → "twitch", "Khalil Rountree x Santo Studio" → "khalil
      // rountree"). Both the product and every pick must carry it: the
      // collections also hold line pieces without the athlete's name (the
      // Khalil collection carries plain TNH tees and an Arman short), and a
      // base tee must not open on an "athlete bundle" of someone else's gear.
      const athleteName = String(this.dataset.athleteName || '')
        .split(/\s+x\s+/i)[0]
        .replace(/\bcapsule\b/i, '')
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .slice(0, 2)
        .join(' ');
      const carriesName = (title) => !!athleteName && String(title || '').toLowerCase().includes(athleteName);
      // A Farm to Table athlete entry on the product is explicit — the
      // product is that athlete's whatever its title says. Collection
      // membership alone is not: then the title must carry the name.
      // (Sibling colourways share the title, so the PDP's title covers them.)
      if (this.dataset.athleteSource !== 'farm-to-table' && !carriesName(this.dataset.productTitle)) return base;

      const products = await this.athleteProducts();
      if (!products.length) return base;

      const isEyewear = (p) => /sunglass|eyewear|glasses/i.test(`${p?.product_type || ''} ${p?.title || ''}`);
      const currentIsEyewear = isEyewear({ product_type: this.dataset.productType, title: this.dataset.productTitle });
      // never a second pair of glasses in a sunglasses look
      const allowEyewear = this.dataset.athleteSunglasses !== 'false' && !currentIsEyewear;
      // never the product itself or one of its colourways
      const exclude = new Set([String(productId), this.productId, ...String(this.dataset.siblingIds || '').split(',').map((v) => v.trim())].filter(Boolean));
      // storefront payload: variants carry `available`, not inventory fields
      const pool = products
        .filter((p) => p && carriesName(p.title))
        .map((p) => ({ ...p, variants: (p.variants || []).filter((v) => v && v.available !== false) }))
        .filter((p) => !exclude.has(String(p.id)) && p.variants.length && wellStocked(p))
        .filter((p) => ['tops', 'outer', 'bottoms', 'footwear'].includes(categoryOf(p)) || (allowEyewear && isEyewear(p)));
      if (pool.length < 2) return base;

      // which two kinds of garment a look needs: what the Rebuy look pairs
      // with this product, else by the product's own kind
      const current = { title: this.dataset.productTitle || '', product_type: this.dataset.productType || '', options: ['Color'], variants: [{ option1: this.dataset.productColor || '' }] };
      let wanted = (base[0] || []).map((pick) => categoryOf({ product_type: pick.type, title: pick.title })).filter((c) => c !== 'other' && c !== 'accessories');
      if (currentIsEyewear) {
        // the glasses are the accessory: dress a full outfit around them
        wanted = ['tops', 'bottoms'];
      } else if (wanted.length !== 2 || wanted[0] === wanted[1]) {
        const own = categoryOf(current);
        wanted = own === 'footwear' ? ['tops', 'bottoms'] : own === 'bottoms' ? ['tops', 'footwear'] : ['bottoms', 'footwear'];
      }
      wanted.sort((a, b) => ((WEAR_ORDER.indexOf(a) + 1) || 99) - ((WEAR_ORDER.indexOf(b) + 1) || 99)); // head to toe

      // As many looks as the athlete's line can fill, no item twice. A look
      // needs a pair that reads as one outfit: the same line (shared title
      // words: "Tempo S Angel Tech" tee + short) or the same colour family
      // (neutrals together count). When no such pair is left — a brown tee
      // and a cream soccer short — the athlete's sunglasses take the top tile
      // instead, if allowed; else the athlete looks stop and Rebuy's follow.
      const currentFamily = familyOf(colourOf(current));
      const used = new Set();
      let athleteLooks = [];
      const kindOf = (p) => (isEyewear(p) ? 'eyewear' : categoryOf(p));
      // a jacket is an upper piece too: it fills the top tile after tees/tanks
      const kindsFor = (kind) => (kind === 'tops' ? ['tops', 'outer'] : [kind]);
      const avail = (kind) => kindsFor(kind).flatMap((k) => pool.filter((p) => !used.has(p.id) && kindOf(p) === k));
      // one sunglasses look per carousel (they are the accessory, not a
      // second outfit); which pair shows takes turns across page views
      const eyewearAvail = () =>
        allowEyewear && !athleteLooks.some((look) => look.some((p) => isEyewear(p)))
          ? rotate(pool.filter((p) => !used.has(p.id) && kindOf(p) === 'eyewear'), view)
          : [];
      const fitsCurrent = (p) => {
        const f = familyOf(colourOf(p));
        return !f || f === currentFamily || NEUTRAL_FAMILIES.has(f);
      };
      // Style group from the title / type, and which groups dress together.
      // tech = performance pieces, only with tech. sport = soccer/football
      // shorts and jerseys: never with a shoe (the sneakers are clean leather,
      // sportswear reads wrong next to them). lounge = fleece, hoodies, sweats. tailored =
      // flannel, shirt jackets, Milano/Napoli, denim, chinos, painter and
      // cargo pants. casual = tees, tanks, knits: goes with everything but
      // tech. surf = boardshorts and rashguards, water wear: only with each
      // other and never in a look with a shoe. A flannel jacket over a
      // soccer short, or a knit over a tech short, never pairs.
      const styleOf = (p) => {
        const t = `${p?.product_type || ''} ${p?.title || ''}`.toLowerCase();
        if (/boardshort|board short|rashguard|rash guard|wetsuit|swim/.test(t)) return 'surf';
        if (/\btech\b|tempo|runner|\brun\b|liner|training|performance/.test(t)) return 'tech';
        if (/soccer|football|jersey/.test(t)) return 'sport';
        if (/fleece|hoodie|sweatpant|sweatshirt|crewneck|jogger/.test(t)) return 'lounge';
        if (/flannel|shirt jacket|milano|napoli|denim|jean|chino|painter|cargo|trouser|overshirt|work ?jacket/.test(t)) return 'tailored';
        return 'casual';
      };
      // Running wear (tanks, liner shorts) is for the track, not for a
      // lifestyle sneaker: never in a look with a shoe. A tech tee or a
      // track set (zip jacket + pant) is fine with one.
      const isRunWear = (p) => /\btank\b|liner|\brun\b|running/.test(`${p?.product_type || ''} ${p?.title || ''}`.toLowerCase());
      const stylesGo = (a, b) => {
        const sa = styleOf(a);
        const sb = styleOf(b);
        if (sa === sb) return true;
        if (sa === 'tech' || sb === 'tech' || sa === 'surf' || sb === 'surf') return false;
        return sa === 'casual' || sb === 'casual';
      };
      // Same line = shared title words once the athlete's name and the
      // garment nouns are removed ("Tempo S Angel Tech" tee + short, "Milano"
      // shirt jacket + pant). The name is on every piece, so counting it made
      // a knit jersey and a tech short look like one line.
      // "Tempo" and "Tech" sit on the whole tech range, so they say nothing
      // about the line: without this the Fade Tech Pant tied with the tee's
      // own S Angel Tech Short and won on collection order.
      const GENERIC_LINE_WORDS = new Set(['tempo', 'tech', 'santo', 'studio']);
      const nameWords = new Set(athleteName.split(' '));
      const lineTokens = (p) => new Set([...titleTokens(p?.title)].filter((w) => !nameWords.has(w) && !GENERIC_LINE_WORDS.has(w)));
      const sameLine = (a, b) => {
        const ta = lineTokens(a);
        const tb = lineTokens(b);
        let shared = 0;
        for (const w of ta) if (tb.has(w)) shared += 1;
        if (!shared) return false;
        const extra = ta.size + tb.size - 2 * shared;
        return shared >= 2 || shared * 3 >= extra;
      };
      // How well two picks read as one outfit. Shoes and sunglasses go with
      // anything. Two garments: the same line first, else the same style
      // family in the same colour family. Mixed styles or clashing colours do
      // not pair at all.
      // colours agree when they share a family, one is unknown, or one is a
      // neutral (black / white / cream / grey / tan go with anything)
      const coloursAgree = (a, b) => {
        const fa = familyOf(colourOf(a));
        const fb = familyOf(colourOf(b));
        return !fa || !fb || fa === fb || NEUTRAL_FAMILIES.has(fa) || NEUTRAL_FAMILIES.has(fb);
      };
      const sameFamily = (a, b) => {
        const fa = familyOf(colourOf(a));
        return !!fa && fa === familyOf(colourOf(b));
      };
      const garmentsGo = (a, b) => sameLine(a, b) || (stylesGo(a, b) && coloursAgree(a, b));
      const pairScore = (a, b) => {
        if (isEyewear(a) || isEyewear(b)) return 1;
        if (categoryOf(a) === 'footwear' || categoryOf(b) === 'footwear') {
          if (isRunWear(a) || isRunWear(b)) return 0;
          return 2 + (sameFamily(a, b) ? 1 : 0);
        }
        if (sameLine(a, b)) return 4 + (sameFamily(a, b) ? 1 : 0);
        if (stylesGo(a, b) && coloursAgree(a, b)) return 2 + (sameFamily(a, b) ? 1 : 0) + (styleOf(a) === styleOf(b) ? 0.5 : 0);
        return 0;
      };
      // The look is the product PLUS the two picks, so a garment pick must go
      // with the product too: a tech tee's look gets the tech short, not the
      // flannel-line pant. Shoes and sunglasses are exempt; a shoe product
      // goes with anything.
      const currentIsGarment = !['footwear', 'accessories', 'other'].includes(categoryOf(current));
      // Water wear (boardshorts, rashguards) and sportswear (soccer shorts,
      // jerseys) never go in a look that has a shoe in it, whether the shoe
      // is the product or a pick.
      if (categoryOf(current) === 'footwear' || wanted.includes('footwear')) {
        for (let i = pool.length - 1; i >= 0; i -= 1) if (styleOf(pool[i]) === 'surf' || styleOf(pool[i]) === 'sport') pool.splice(i, 1);
      }
      // Running wear is a preference, not a wall: on a shoe page it is
      // benched first, and only put back (liner shorts, never tanks) when the
      // athlete's line would otherwise have no look at all — Khalil's only
      // bottom in depth is his No Risk liner short.
      const benched = [];
      if (categoryOf(current) === 'footwear') {
        for (let i = pool.length - 1; i >= 0; i -= 1) if (isRunWear(pool[i])) benched.push(...pool.splice(i, 1));
        // a line with no other bottom at all (Khalil: the Napoli pant is out
        // of depth) keeps its liner shorts from the start — tanks stay out
        if (!pool.some((q) => categoryOf(q) === 'bottoms')) {
          const liners = benched.filter((b) => !/\btank\b/.test(`${b.product_type || ''} ${b.title || ''}`.toLowerCase()));
          pool.push(...liners);
          benched.length = 0;
        }
      }
      // The look is the product plus the picks, so a garment pick also keeps
      // to the product's own colour family or a neutral — the rule the Rebuy
      // looks follow. A shoe or sunglasses pick is exempt, and a shoe product
      // goes with anything (a black and a white Trento take the same set).
      // Without this the black Twitch 2 Sunglasses opened on the calm-brown
      // Angel tee + short, the only tee and short in the line: a third
      // colour next to the product. When no pick in the line fits, the
      // athlete looks stop and Rebuy's (already colour-gated) follow.
      const currentIsShoe = categoryOf(current) === 'footwear';
      const colourFits = (p) => currentIsShoe || isEyewear(p) || categoryOf(p) === 'footwear' || fitsCurrent(p);
      const fitsProduct = (p) => colourFits(p) && (!currentIsGarment || isEyewear(p) || categoryOf(p) === 'footwear' || garmentsGo(current, p));
      const productLine = (p) => (currentIsGarment && !isEyewear(p) && categoryOf(p) !== 'footwear' && sameLine(current, p) ? 1 : 0);
      // Build every coherent look the line can fill; the order rotates on
      // every page view (below).
      const buildLooks = () => {
        used.clear();
        athleteLooks = [];
        while (athleteLooks.length < this.maxLooks + 6) {
        const A = avail(wanted[0]).filter(fitsProduct);
        const B = avail(wanted[1]).filter(fitsProduct);
        let best = null;
        for (const a of A) {
          for (const b of B) {
            let score = pairScore(a, b);
            if (!score) continue;
            // the product's own line first (the tee's matching short)
            score += productLine(a) + productLine(b);
            if (!best || score > best.score) best = { pair: [a, b], score };
          }
        }
        // Layering: an outer piece over its own top (the No Risk hoodie over
        // the No Risk tee) is a fit too — same line only, so a track jacket
        // never lands over an unrelated tee. Ranked just under a full top +
        // bottom set. The outer layer shows first.
        const topsSlot = wanted.includes('tops');
        const layerPairs = (outers, tops, bonus) => {
          let found = null;
          for (const o of outers) {
            for (const t of tops) {
              if (o.id === t.id || categoryOf(o) !== 'outer' || categoryOf(t) === 'outer') continue;
              if (!sameLine(o, t)) continue;
              const base = pairScore(o, t);
              if (!base) continue;
              const score = base + bonus + productLine(o) + productLine(t);
              if (!found || score > found.score) found = { pair: [o, t], score };
            }
          }
          return found;
        };
        if (topsSlot) {
          const T = avail('tops').filter(fitsProduct);
          const layered = layerPairs(T, T, -0.5);
          if (layered && (!best || layered.score > best.score)) best = layered;
        }
        let pair = best ? best.pair : null;
        if (!pair) {
          // no coherent pair left: the athlete's sunglasses complete a
          // leftover piece — a jacket first (a dress jacket + shades reads
          // as a fit), else a bottom
          const E = eyewearAvail();
          const jacketsFirst = (list) => list.filter((p) => categoryOf(p) === 'outer').concat(list.filter((p) => categoryOf(p) !== 'outer'));
          const Af = jacketsFirst(A.filter((p) => !isEyewear(p) && categoryOf(p) !== 'footwear'));
          const Bf = B.filter((p) => !isEyewear(p) && categoryOf(p) !== 'footwear');
          if (E.length && Af.length) pair = [Af[0], E[0]];
          else if (E.length && Bf.length) pair = [E[0], Bf[0]];
        }
        if (!pair && wanted.includes('bottoms')) {
          // Thin line, last resort: a bottom may repeat so another of the
          // athlete's tops still gets a look — Khalil's No Risk set is a tee,
          // a tank, a hoodie and ONE liner short, and without this the hoodie
          // fit never forms. The top is always a fresh piece.
          const bi = wanted.indexOf('bottoms');
          const fresh = avail(wanted[1 - bi]).filter((p) => fitsProduct(p) && !isEyewear(p));
          const reuse = kindsFor('bottoms').flatMap((k) => pool.filter((p) => kindOf(p) === k)).filter(fitsProduct);
          let again = null;
          for (const a of fresh) {
            for (const b of reuse) {
              const score = pairScore(a, b) + productLine(a) + productLine(b);
              if (pairScore(a, b) && (!again || score > again.score)) again = { pair: bi === 0 ? [b, a] : [a, b], score };
            }
          }
          if (topsSlot) {
            // a fresh hoodie over the tee already shown, or a fresh tee under
            // the hoodie already shown
            const freshTops = avail('tops').filter(fitsProduct);
            const anyTops = kindsFor('tops').flatMap((k) => pool.filter((p) => kindOf(p) === k)).filter(fitsProduct);
            const layered = layerPairs(freshTops, anyTops, 0.5) || layerPairs(anyTops, freshTops, 0.5);
            if (layered && (!again || layered.score > again.score)) again = layered;
          }
          if (again) pair = again.pair;
        }
        if (!pair) break;
        pair.forEach((p) => used.add(p.id));
        // sunglasses read as the top piece: keep them left of a garment
        pair.sort((a, b) => (isEyewear(a) ? -1 : 0) - (isEyewear(b) ? -1 : 0));
        athleteLooks.push(pair.map((p) => this.pickData(p)));
        }
      };
      buildLooks();
      if (!athleteLooks.length && benched.length) {
        const liners = benched.filter((b) => !/\btank\b/.test(`${b.product_type || ''} ${b.title || ''}`.toLowerCase()));
        if (liners.length) {
          pool.push(...liners);
          buildLooks();
        }
      }
      if (!athleteLooks.length) return base;

      // One rule for the whole carousel on an athlete page: athlete looks
      // first, Rebuy's looks after (minus anything already shown), and the
      // full list advances by one position on every page view, so each
      // reload opens on the next fit.
      const shown = new Set(athleteLooks.flat().map((p) => p.id));
      const rest = base.filter((look) => !look.some((p) => shown.has(String(p.id))));
      const all = [...athleteLooks, ...rest].slice(0, this.maxLooks);
      return rotate(all, view % all.length);
    }

    cacheKey() {
      return this.cacheKeyFor(this.productId);
    }

    cacheKeyFor(productId) {
      return `${STORAGE_PREFIX}:v${CACHE_VERSION}:${this.dataset.dataSource}:${productId}`;
    }

    readCache() {
      try {
        const raw = sessionStorage.getItem(this.cacheKey());
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || Date.now() - parsed.t > CACHE_TTL_MS || !Array.isArray(parsed.looks)) return null;
        return parsed.looks;
      } catch {
        return null;
      }
    }

    writeCache(looks) {
      try {
        sessionStorage.setItem(this.cacheKey(), JSON.stringify({ t: Date.now(), looks }));
      } catch {}
    }

    async fetchLooks() {
      // One data source: pair one product per rule-output collection (Rebuy
      // fills outputs IN ORDER, so the second collection only shows up when
      // the first is small). Two or more, comma-separated: one data source
      // per tile, in order — e.g. bottoms, footwear — which is the reliable
      // way to get "shorts + shoes" on every look.
      const sources = (this.dataset.dataSource || '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      if (!sources.length) return null;
      // Pure API: the storefront key comes from the section setting, so the
      // call fires as soon as this script runs — nothing here waits on or
      // reads Rebuy's app-embed script.
      const key = (this.dataset.rebuyKey || '').trim();
      if (!key) {
        console.warn('Wear it with: no Rebuy API key in the section settings');
        return null;
      }

      // single source: room for two rules of ~10 products each
      // A single data source answers with every matched rule's output in rule
      // order and truncates at `limit`, so the LAST rule's collection is what
      // gets cut: with two rules at Product Limit 24, a limit of 32 left the
      // tee slot 8 products. 48 covers two rules at 24 (~18 KB with `fields`).
      // The rules' Product Limits decide WHICH products Rebuy sends (in its own
      // order, never by price or sales), so for "highest price" or "best
      // selling" to mean anything the limits must cover the whole slot
      // collection (Lifestyle Pants 35 + Lifestyle Tees 94) and this cap must
      // not cut them: 160. Rebuy gzips the response (~15 KB per 48 products
      // with `fields`), so a full pool is ~40 KB on the wire.
      const limit = sources.length > 1 ? Math.max(12, this.maxLooks * 4) : Math.max(160, this.maxLooks * 16);
      const [salesRank, responses] = await Promise.all([
        this.fetchSalesRank(key),
        Promise.all(sources.map((source) => this.fetchSource(source, key, limit, this.productId))),
      ]);
      this.salesRank = salesRank;
      if (responses.every((r) => !r)) return null;

      return this.looksFromResponses(responses, this.productId, {
        title: this.dataset.productTitle || '',
        options: ['Color'],
        variants: [{ option1: this.dataset.productColor || '' }],
      });
    }

    // Sales rank as Map(productId -> position). Rebuy returns a collection in
    // its own order whatever Shopify sort it has, so this is where "what
    // sells" comes from. Two sources:
    //   - Shopify's best-selling order (templates/collection.wiw-rank.liquid,
    //     ~0.2 s, all-time): what THIS page renders with.
    //   - Rebuy's 90-day top sellers (recency the season needs, but 8-9 s at
    //     250 products): fetched in the background and session-cached, so
    //     the NEXT product page in the session ranks by recent sales. Never
    //     on the render path.
    // Null = fall back to Rebuy's order.
    async fetchSalesRank(key) {
      const toMap = (ids) => new Map(ids.map((id, i) => [String(id), i]));
      try {
        const raw = sessionStorage.getItem(RANK_CACHE_KEY);
        if (raw) {
          const { t, ids } = JSON.parse(raw);
          if (Date.now() - t < CACHE_TTL_MS && Array.isArray(ids) && ids.length) return toMap(ids);
        }
      } catch (e) {
        /* storage unavailable */
      }
      if (key && !fetchSalesRankWarm.started) {
        fetchSalesRankWarm.started = true;
        fetch(`${REBUY_TOP_SELLERS}?key=${encodeURIComponent(key)}&limit=250&days=90&fields=id`)
          .then((res) => (res.ok ? res.json() : null))
          .then((json) => {
            const ids = (json?.data || []).map((p) => String(p.id));
            if (ids.length) sessionStorage.setItem(RANK_CACHE_KEY, JSON.stringify({ t: Date.now(), ids }));
          })
          .catch(() => {});
      }
      try {
        const res = await withTimeout(
          fetch(`${storefrontRoot()}collections/all?view=wiw-rank&sort_by=best-selling`, { credentials: 'same-origin' }),
          RANK_BUDGET_MS,
        );
        if (!res.ok) throw new Error(`rank ${res.status}`);
        const { ids } = JSON.parse(await res.text());
        return Array.isArray(ids) && ids.length ? toMap(ids) : null;
      } catch (err) {
        console.warn('Wear it with: sales rank unavailable, using Rebuy order', err);
        return null;
      }
    }

    // stable sort by sales rank; products outside the top list keep Rebuy's
    // relative order after the ranked ones
    // Section setting "Rank picks by":
    //   sales            — recent best sellers first
    //   full_price_first — anything not marked down first (new season, full
    //                      margin), each tier by recent sales
    //   newest           — published in the last NEW_DAYS first, then the rest,
    //                      each tier by recent sales
    //   price            — most expensive first, ties by recent sales
    // Recent sales always break ties; with no rank, Rebuy's order does.
    salesOrder(list) {
      const mode = this.dataset.rankMode || DEFAULT_RANK_MODE;
      const rank = this.salesRank;
      const salesOf = (p) => (rank && rank.has(String(p.id)) ? rank.get(String(p.id)) : Infinity);
      if (!rank && mode === 'sales') return list;
      const keyOf = (p) => {
        if (mode === 'price') return [-maxPrice(p), salesOf(p)];
        if (mode === 'full_price_first') return [isMarkedDown(p) ? 1 : 0, salesOf(p)];
        if (mode === 'newest') return [isNew(p) ? 0 : 1, salesOf(p)];
        return [salesOf(p)];
      };
      return list
        .map((p, i) => ({ p, i, k: keyOf(p) }))
        .sort((a, b) => {
          for (let j = 0; j < a.k.length; j += 1) if (a.k[j] !== b.k[j]) return a.k[j] - b.k[j];
          return a.i - b.i;
        })
        .map((x) => x.p);
    }

    looksFromResponses(responses, productId, fallbackCurrent) {
      const current = responses.find((r) => r?.metadata?.input_products?.[0])?.metadata.input_products[0] || fallbackCurrent;
      if (responses.length === 1) {
        const json = responses[0];
        return this.buildLooks(json.data || [], json.metadata?.matched_rules || [], current, productId);
      }
      // each source is a tile slot; a product listed by several sources stays
      // with the first one that listed it
      const seen = new Set();
      const slots = responses.map((r) => this.pool(r?.data || [], productId).filter((p) => !seen.has(p.id) && seen.add(p.id)));
      return this.pairLooks(slots, [], current);
    }

    // Sibling colourways (the PDP's colour swatches) get their looks fetched
    // in idle time and cached, so switching colour renders at once.
    prefetchSiblings() {
      const ids = (this.dataset.siblingIds || '')
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v && v !== this.productId)
        .slice(0, 8);
      if (!ids.length) return;
      const sources = (this.dataset.dataSource || '').split(',').map((v) => v.trim()).filter(Boolean);
      // A single data source answers with every matched rule's output in rule
      // order and truncates at `limit`, so the LAST rule's collection is what
      // gets cut: with two rules at Product Limit 24, a limit of 32 left the
      // tee slot 8 products. 48 covers two rules at 24 (~18 KB with `fields`).
      const limit = sources.length > 1 ? Math.max(12, this.maxLooks * 4) : Math.max(160, this.maxLooks * 16);
      const run = async () => {
        const key = (this.dataset.rebuyKey || '').trim();
        if (!key) return;
        for (const id of ids) {
          if (sessionStorage.getItem(this.cacheKeyFor(id))) continue;
          const responses = await Promise.all(sources.map((source) => this.fetchSource(source, key, limit, id)));
          if (responses.every((r) => !r)) continue;
          const looks = this.looksFromResponses(responses, id, { title: '', options: ['Color'], variants: [{ option1: '' }] });
          try {
            sessionStorage.setItem(this.cacheKeyFor(id), JSON.stringify({ t: Date.now(), looks }));
          } catch {}
        }
      };
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      idle(() => run().catch(() => {}));
    }

    async fetchSource(source, key, limit, productId) {
      const url = new URL(REBUY_ENDPOINT + encodeURIComponent(source));
      url.searchParams.set('key', key);
      url.searchParams.set('shopify_product_ids', productId);
      // headroom for the colourway collapse and the per-collection pairing
      url.searchParams.set('limit', String(limit));
      url.searchParams.set('filter_oos', 'yes');
      // only what the pairing needs — drops body_html etc., ~45 KB → ~12 KB
      url.searchParams.set('fields', REBUY_FIELDS);
      try {
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`Rebuy ${res.status}`);
        return await res.json();
      } catch (err) {
        console.error(`Wear it with: Rebuy data source ${source} failed`, err);
        return null;
      }
    }

    // purchasable and not the current product (colourways are collapsed
    // later, after ranking, so the matching colour is the one kept)
    pool(products, productId = this.productId) {
      const out = [];
      for (const p of products) {
        if (!p || String(p.id) === String(productId)) continue;
        // Rebuy's "filter out of stock" keeps products that are active in
        // Shopify but never published to the Online Store (published_at
        // null): their page 404s and the quick view can only say
        // "Unavailable". Drop them here; fields missing from the response
        // (older cached payloads) fall through unchanged.
        if (p.status && p.status !== 'active') continue;
        if ('published_at' in p && !p.published_at) continue;
        if (!Array.isArray(p.variants) || !p.variants.some(variantAvailable)) continue;
        if (!wellStocked(p)) continue;
        out.push(p);
      }
      return out;
    }

    buildLooks(products, matchedRules, current, productId = this.productId) {
      const pool = this.pool(products, productId);

      // group by the rules' output collections, in rule / output order
      const outputCollections = [];
      for (const rule of matchedRules) {
        for (const out of rule?.output || []) {
          const id = Number(out?.collection_id);
          if (id && !outputCollections.includes(id)) outputCollections.push(id);
        }
      }
      let groups = outputCollections.map(() => []);
      const rest = [];
      for (const p of pool) {
        const ids = (p.collection_ids || []).map(Number);
        const gi = outputCollections.findIndex((c) => ids.includes(c));
        if (gi >= 0) groups[gi].push(p);
        else rest.push(p);
      }
      // candidates that carry none of the output collections (Rebuy's copy of
      // the product predates the collection) join the slot whose members are
      // the same kind of garment; a kind no slot covers becomes its own slot
      if (rest.length) {
        const dominant = groups.map((g) => {
          const n = {};
          for (const p of g) n[categoryOf(p)] = (n[categoryOf(p)] || 0) + 1;
          return Object.keys(n).sort((a, b) => n[b] - n[a])[0] || '';
        });
        const extra = new Map();
        const orphans = rest.splice(0);
        for (const p of orphans) {
          const cat = categoryOf(p);
          const gi = dominant.indexOf(cat);
          if (gi >= 0) groups[gi].push(p);
          else if (cat !== 'other') {
            if (!extra.has(cat)) extra.set(cat, []);
            extra.get(cat).push(p);
          } else rest.push(p);
        }
        groups.push(...extra.values());
      }
      groups = groups.filter((g) => g.length);

      // Slots that hold the same kind of garment become ONE tile slot: a tee
      // rule that returns Walk Shorts and another that returns Pants both feed
      // the "bottoms" tile, instead of a short being paired with a pant and the
      // shoe falling off the look. The merged slot interleaves its sources so
      // later looks alternate between them; when the product itself is a
      // long-sleeve / knit / fleece piece, long bottoms (pants, denim) come
      // first — long sleeves go with long legs.
      groups = mergeSlotsByCategory(groups, current);
      // best sellers first inside every slot (Rebuy's order only breaks ties)
      groups = groups.map((g) => this.salesOrder(g));

      if (groups.length < 2) {
        // no usable collection split: pair by product type instead
        const byType = new Map();
        for (const p of pool) {
          const k = String(p.product_type || '').trim().toLowerCase();
          if (!byType.has(k)) byType.set(k, []);
          byType.get(k).push(p);
        }
        groups = Array.from(byType.values()).map((g) => this.salesOrder(g));
        rest.length = 0;
      }
      return this.pairLooks(groups, rest, current);
    }

    // groups = one candidate list per tile slot (or per collection / type);
    // each look takes the best remaining product from each group in turn
    pairLooks(groups, rest, current) {
      groups = sortSlotsByWear(groups);
      // matching pieces first inside each group (stable: Rebuy's rank breaks
      // ties). Every colourway stays a candidate; a title is only consumed
      // once it's picked, so the black Tempo Ultra Tee can serve a black look
      // and the white one a white look, but no item shows twice.
      const rankGroups = groups.map((g) => g.slice()); // sales order (Rebuy's when no rank)
      const rankMode = this.dataset.rankMode || DEFAULT_RANK_MODE;
      // Rotation seed. Colourways of one model (data-sibling-ids) share a
      // family seed and take CONSECUTIVE positions in the window, so clicking
      // through the swatches never shows the same opening pant or tee twice —
      // a per-product hash cannot promise that (three Trentos hashed to the
      // same start mod 5). Products without siblings keep their own hash.
      const self = String(current?.id || this.productId);
      const family = [self, ...String(this.dataset.siblingIds || '').split(',').map((v) => v.trim()).filter(Boolean)]
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
      const familyPos = Math.max(0, family.indexOf(self));
      const seed = hashString(family[0]);
      groups = groups.map((g, gi) => {
        const scored = g
          .map((p, i) => ({ p, i, s: current ? similarity(current, p) : 0 }))
          .sort((x, y) => y.s - x.s || x.i - y.i);
        // nothing in this slot shares a line/collab with the product (shoes
        // vs pants): open on one of the slot's top sellers, which one depends
        // on the product, so neighbouring shoes don't all show the same #1
        if (!scored.length || scored[0].s <= 0) {
          const list = scored.map((x) => x.p);
          return rotateTop(list, tierWidth(list, rankMode, TOP_SELLERS), mix(seed + gi * 0x9e3779b9) + familyPos);
        }
        return scored.map((x) => x.p);
      });

      const used = new Set();
      const usedTitles = new Set();
      const titleKey = (p) => String(p.title || '').trim().toLowerCase();
      const isFree = (p) => !used.has(p.id) && !usedTitles.has(titleKey(p));
      const consume = (p) => {
        used.add(p.id);
        usedTitles.add(titleKey(p));
        return p;
      };
      const takeWhere = (arr, pred) => {
        for (const p of arr) {
          if (!isFree(p) || !pred(p)) continue;
          return consume(p);
        }
        return null;
      };
      const currentColour = colourOf(current);
      const currentFamily = familyOf(currentColour);
      const looks = [];

      // Footwear: colour matching is for tees and shorts. A white or black
      // sneaker goes with anything, and cycling colour families here only
      // forced the pool's one white pant onto every white Trento. Walk the
      // slots in order instead — same-line pieces first (a Khalil shoe still
      // gets the Khalil tee), then the ranked order, rotated per product —
      // with one guard: not two loud colours in the same look.
      if (categoryOf(current) === 'footwear') {
        const loud = (p) => {
          const f = familyOf(colourOf(p));
          return f && f !== 'other' && !NEUTRAL_FAMILIES.has(f);
        };
        const lists = groups.length >= 2 ? groups.slice(0, 2) : [groups[0] || [], groups[0] || []];
        while (looks.length < this.maxLooks) {
          const a = takeWhere(lists[0], () => true);
          if (!a) break;
          const b =
            takeWhere(lists[1], (p) => p.id !== a.id && titleKey(p) !== titleKey(a) && !(loud(a) && loud(p))) ||
            takeWhere(lists[1], (p) => p.id !== a.id && titleKey(p) !== titleKey(a));
          if (!b) break;
          looks.push([a, b]);
        }
        return looks.map((picks) => picks.map((p) => this.pickData(p)));
      }

      // Look 1: the best match in each slot (line/collab first, then Rebuy's
      // rank), as long as the pick is in the product's colour family or a
      // neutral — any colour only if nothing else is available.
      {
        const picks = [];
        for (const g of groups) {
          if (picks.length === 2) break;
          const p =
            takeWhere(g, (c) => {
              const f = familyOf(colourOf(c));
              return !f || f === currentFamily || NEUTRAL_FAMILIES.has(f);
            }) || takeWhere(g, () => true);
          if (p) picks.push(p);
        }
        if (picks.length < 2) {
          for (const g of [...groups, rest]) {
            if (picks.length === 2) break;
            const p = takeWhere(g, () => true);
            if (p) picks.push(p);
          }
        }
        if (picks.length) looks.push(picks);
      }

      // Later looks: one colour family per look. The outfit includes the
      // product itself, so every pick is either its own colour family or a
      // neutral — no third colour gets introduced (a rose tee on fade-blue
      // shorts). Family order = own family, then the neutrals rotated per
      // product; a family already shown is skipped until nothing else can
      // fill the carousel.
      const slots = groups.length >= 2 ? groups.slice(0, 2) : [groups[0] || [], groups[0] || []];
      const rankSlots = rankGroups.length >= 2 ? rankGroups.slice(0, 2) : [rankGroups[0] || [], rankGroups[0] || []];
      const famOf = (p) => familyOf(colourOf(p));
      const familyOrder = [];
      const pushFamily = (f) => {
        if (f && !familyOrder.includes(f)) familyOrder.push(f);
      };
      pushFamily(currentFamily);
      // A family look needs depth: with one white pant in the pool, a "white
      // look" put the same White Flower Milano on every white, cream and
      // black Trento. Families with fewer than MIN_FAMILY_POOL candidates in
      // a slot are skipped in the strict pass (they only fill when nothing
      // else can). A neutral product (a white or black shoe) goes with any
      // colour, so the coloured families that DO have depth — olive, navy,
      // merlot pants — join the rotation and carry the variety.
      const familyCount = (slotIndex, f) => slots[slotIndex].filter((p) => famOf(p) === f).length;
      const deepFamilies = new Set();
      for (const si of [0, 1]) for (const p of slots[si]) {
        const f = famOf(p);
        if (f && familyCount(si, f) >= MIN_FAMILY_POOL) deepFamilies.add(f);
      }
      if (!currentFamily || NEUTRAL_FAMILIES.has(currentFamily)) {
        const coloured = [...deepFamilies].filter((f) => !NEUTRAL_FAMILIES.has(f) && f !== 'other').sort();
        rotate(coloured, (seed >> 8) + familyPos).forEach(pushFamily);
      }
      rotate(['black', 'white', 'cream', 'grey', 'tan'], seed + familyPos).forEach(pushFamily);
      let allowRare = false;

      const usedFamilies = new Set();
      for (const p of looks[0] || []) {
        const f = famOf(p);
        if (f) usedFamilies.add(f);
      }

      // own family keeps the line-match order (the matching set); every other
      // family takes one of its top sellers, which one depends on the product
      const pickFrom = (slotIndex, pred, own) => {
        if (own) return slots[slotIndex].find(pred);
        const list = rankSlots[slotIndex].filter(pred);
        return rotateTop(list, tierWidth(list, rankMode, TOP_SELLERS), mix(seed + slotIndex * 0x9e3779b9) + familyPos)[0];
      };
      const partnerOk = (a) => (p) => isFree(p) && p.id !== a.id && titleKey(p) !== titleKey(a);
      const isAnchor = (p) => ['black', 'white'].includes(famOf(p));

      const inFamily = (slotIndex, f) => (p) => famOf(p) === f && (allowRare || familyCount(slotIndex, f) >= MIN_FAMILY_POOL);
      const tonalPair = (f, own) => {
        const first = pickFrom(0, (p) => isFree(p) && inFamily(0, f)(p), own);
        if (!first) return null;
        const second = pickFrom(1, (p) => partnerOk(first)(p) && inFamily(1, f)(p), own);
        return second ? [first, second] : null;
      };
      // a coloured piece with a black or white partner — black and white go
      // with anything, which is what keeps cream away from merlot
      const anchoredPair = (f, own) => {
        for (const [ai, bi] of [[0, 1], [1, 0]]) {
          const a = pickFrom(ai, (p) => isFree(p) && inFamily(ai, f)(p), own);
          if (!a) continue;
          const b = pickFrom(bi, (p) => partnerOk(a)(p) && isAnchor(p), false);
          if (b) return ai === 0 ? [a, b] : [b, a];
        }
        return null;
      };
      const tryFamily = (f) => {
        const own = f === currentFamily;
        // the tonal set first (black tee + black jacket), else the family
        // piece on a black/white anchor (white tee + black jacket)
        return tonalPair(f, own) || anchoredPair(f, own);
      };

      for (const strict of [true, false]) {
        allowRare = !strict;
        for (const f of familyOrder) {
          if (looks.length >= this.maxLooks || !rankSlots[0].length) break;
          if (strict && usedFamilies.has(f)) continue;
          const pair = tryFamily(f);
          if (!pair) continue;
          looks.push(pair.map(consume));
          usedFamilies.add(f);
        }
      }

      return looks.map((picks) => picks.map((p) => this.pickData(p)));
    }

    pickData(p) {
      return {
        id: String(p.id),
        handle: String(p.handle || ''),
        title: String(p.title || ''),
        type: String(p.product_type || ''),
        image: String(p.image?.src || p.images?.[0]?.src || ''),
      };
    }

    render(looks) {
      const frag = document.createDocumentFragment();
      looks.forEach((picks, i) => {
        const look = this.template.content.firstElementChild.cloneNode(true);
        look.setAttribute('aria-label', `Look ${i + 1} of ${looks.length}`);
        const row = look.querySelector('[data-wiw-row]');
        // the first look is what the shopper scrolls to: fetch its photos now
        if (row) row.innerHTML = picks.map((p) => this.tileHTML(p, i === 0 ? 'eager' : 'lazy')).join('');
        if (i === 0) look.querySelectorAll('.wiw__tile--hero img').forEach((img) => (img.loading = 'eager'));
        frag.appendChild(look);
      });
      this.track.appendChild(frag);
      this.looks = Array.from(this.track.querySelectorAll('.wiw__look'));
      // lets the stylesheet centre a short row (1 look on tablet, 1-2 on desktop)
      this.dataset.looks = String(looks.length);
      this.hidden = false;
      this.initPager();
      this.prefetchSiblings();
    }

    tileHTML(p, loading = 'lazy') {
      const img = sizedImage(p.image, 600);
      return `
        <button type="button" class="wiw__tile wiw__tile--rec" data-handle="${escapeHtml(p.handle)}" data-product-id="${escapeHtml(p.id)}" aria-label="Quick view: ${escapeHtml(p.title)}" aria-haspopup="dialog">
          ${img ? `<img class="wiw__img" src="${escapeHtml(img)}" alt="" loading="${loading}">` : ''}
          <span class="wiw__dots" aria-hidden="true"><i></i><i></i><i></i></span>
        </button>`;
    }

    // Pager: counter = nearest scroll stop (one per look), fill = visible share
    // positioned by scroll progress — the tenthousand pattern from plp-visual-nav.
    initPager() {
      this.stops = [0];
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', () => window.requestAnimationFrame(this.update), { passive: true });
      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.update();
      });
      this.resizeObserver.observe(this.track);
      this.measure();
      this.update();
    }

    measure() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.max = max;
      this.dataset.overflow = max > 1 ? 'true' : 'false';
      const origin = this.looks[0]?.offsetLeft || 0;
      const stops = [];
      for (const look of this.looks) {
        const stop = Math.min(max, look.offsetLeft - origin);
        if (!stops.length || stop - stops[stops.length - 1] > 1) stops.push(stop);
        if (stop >= max) break;
      }
      this.stops = stops.length ? stops : [0];
    }

    update() {
      if (!(this.max > 1)) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const share = clientWidth / scrollWidth;
      const progress = Math.min(1, Math.max(0, scrollLeft / this.max));
      if (this.fill) {
        this.fill.style.width = share * 100 + '%';
        this.fill.style.transform = 'translateX(' + (progress * (1 - share) * 100) / share + '%)';
      }
      let index = 0;
      this.stops.forEach((stop, i) => {
        if (Math.abs(stop - scrollLeft) < Math.abs(this.stops[index] - scrollLeft)) index = i;
      });
      if (this.counter) this.counter.textContent = index + 1 + '/' + this.stops.length;
    }

    onClick(event) {
      const tile = event.target.closest('.wiw__tile--rec');
      if (!tile || !this.contains(tile) || !this.quickView) return;
      event.preventDefault();
      this.quickView.open(tile.dataset.handle, tile);
    }
  }

  class WearItWithQuickView extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;
      this.panel = this.querySelector('[data-wiw-qv-panel]');
      this.body = this.querySelector('[data-wiw-qv-body]');
      this.loadError = this.querySelector('[data-wiw-qv-load-error]');
      this.sectionId = this.dataset.section || 'ss-wear-it-with-quick-view';
      this.cache = new Map();
      this.popups = [];
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      // a feature-chip popup (theme modal-dialog) closing drops the body
      // scroll lock and the focus trap we rely on — put them back
      this.onModalClosed = () => {
        if (this.hidden) return;
        document.body.classList.add('overflow-hidden');
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
      };
    }

    async open(handle, opener) {
      if (!handle) return;
      this.opener = opener || null;
      this.handle = handle;
      this.hidden = false;
      this.classList.add('wiw-qv--loading');
      this.classList.remove('wiw-qv--load-error');
      if (this.loadError) this.loadError.hidden = true;
      document.body.classList.add('overflow-hidden');
      document.body.addEventListener('modalClosed', this.onModalClosed);
      // focus the dialog itself (tabindex=-1), like the theme's modal-dialog:
      // CLOSE only draws its focus ring once a keyboard user tabs to it
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);

      const token = (this.token = {});
      let html = null;
      try {
        html = await this.fetchContent(handle);
      } catch (err) {
        console.error('Wear it with: quick view failed', err);
      }
      if (this.token !== token || this.hidden) return;
      if (!html) {
        this.classList.add('wiw-qv--load-error');
        if (this.loadError) this.loadError.hidden = false;
        return;
      }
      this.mount(html);
    }

    async fetchContent(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      const url = `${storefrontRoot()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.sectionId)}`;
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Quick view ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const content = doc.querySelector('.wiw-qv__content');
      if (!content) throw new Error('Quick view: no content in section response');
      const html = content.outerHTML;
      this.cache.set(handle, html);
      return html;
    }

    mount(html) {
      this.cleanupPopups();
      this.body.innerHTML = html;
      this.classList.remove('wiw-qv--loading');
      this.content = this.body.querySelector('.wiw-qv__content');
      if (!this.content) return;
      const productId = this.content.dataset.productId || '';
      this.panel.setAttribute('aria-label', this.content.dataset.productTitle || 'Quick view');
      this.panel.scrollTop = 0;
      // the chip popups (theme <modal-dialog>) moved themselves to <body> on
      // insert; remember them so closing the sheet removes them again
      this.popups = productId
        ? Array.from(document.body.querySelectorAll(`modal-dialog[id^="SfsFeaturePopup-${CSS.escape(productId)}-"]`))
        : [];
      this.initGallery();
      this.initSizes();
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
    }

    cleanupPopups() {
      for (const popup of this.popups) {
        if (popup.hasAttribute('open') && typeof popup.hide === 'function') popup.hide();
        popup.remove();
      }
      this.popups = [];
    }

    close({ restoreFocus = true } = {}) {
      if (this.hidden) return;
      this.token = null;
      this.hidden = true;
      this.classList.remove('wiw-qv--loading', 'wiw-qv--load-error');
      document.body.classList.remove('overflow-hidden');
      document.body.removeEventListener('modalClosed', this.onModalClosed);
      this.cleanupPopups();
      this.body.innerHTML = '';
      this.content = null;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(restoreFocus ? this.opener : null);
      this.opener = null;
    }

    initGallery() {
      const gallery = this.content.querySelector('[data-wiw-gallery]');
      const slides = gallery?.querySelector('[data-wiw-slides]');
      const fill = gallery?.querySelector('[data-wiw-gallery-fill]');
      if (!slides || !fill) return;
      const update = () => {
        const max = slides.scrollWidth - slides.clientWidth;
        const share = slides.scrollWidth ? slides.clientWidth / slides.scrollWidth : 1;
        const progress = max > 0 ? Math.min(1, Math.max(0, slides.scrollLeft / max)) : 0;
        fill.style.width = share * 100 + '%';
        fill.style.transform = 'translateX(' + (progress * (1 - share) * 100) / share + '%)';
      };
      slides.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      update();
    }

    initSizes() {
      this.sizeButtons = Array.from(this.content.querySelectorAll('[data-wiw-size]'));
      const checked = this.sizeButtons.find((b) => b.getAttribute('aria-checked') === 'true');
      this.applyVariant(checked ? checked.dataset : this.content.dataset);
    }

    selectSize(button) {
      for (const b of this.sizeButtons) {
        const on = b === button;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      }
      const label = this.content.querySelector('[data-wiw-size-value]');
      if (label) label.textContent = button.dataset.size || '';
      this.applyVariant(button.dataset);
      this.setError('');
    }

    applyVariant(d) {
      this.variantId = d.variantId || '';
      this.sellingPlan = d.sellingPlan || '';
      const available = d.available === 'true';
      const preorder = d.preorder === 'true';
      const onSale = Boolean(d.compareText);

      const discount = this.content.querySelector('[data-wiw-discount]');
      const discountText = this.content.querySelector('[data-wiw-discount-text]');
      const compare = this.content.querySelector('[data-wiw-compare]');
      const price = this.content.querySelector('[data-wiw-price]');
      if (discount) discount.hidden = !onSale;
      if (discountText) discountText.textContent = onSale ? `${d.percentOff}% OFF` : '';
      if (compare) {
        compare.hidden = !onSale;
        compare.textContent = d.compareText || '';
      }
      if (price) {
        price.textContent = d.priceText || '';
        price.classList.toggle('wiw-qv__price--sale', onSale);
      }

      const atc = this.content.querySelector('[data-wiw-atc]');
      const atcLabel = this.content.querySelector('[data-wiw-atc-label]');
      if (atc) {
        atc.disabled = !this.variantId || !available;
        atc.classList.remove('is-loading');
      }
      if (atcLabel) {
        const strings = window.variantStrings || {};
        if (!this.variantId) atcLabel.textContent = strings.unavailable || 'Unavailable';
        else if (!available) atcLabel.textContent = strings.soldOut || 'Sold out';
        else if (preorder) atcLabel.textContent = 'Preorder';
        else atcLabel.textContent = strings.addToCart || 'Add to cart';
      }
    }

    setError(message) {
      const el = this.content?.querySelector('[data-wiw-qv-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    async addToCart(button) {
      if (!this.variantId || button.disabled) return;
      button.disabled = true;
      button.classList.add('is-loading');
      this.setError('');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const item = { id: Number(this.variantId), quantity: 1 };
      if (this.sellingPlan) item.selling_plan = Number(this.sellingPlan);
      const payload = { items: [item] };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
      config.body = JSON.stringify(payload);

      try {
        const res = await fetch(window.routes?.cart_add_url || '/cart/add', config);
        const json = await res.json();
        if (json.status) throw new Error(json.description || json.message || `Add failed: ${res.status}`);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wear-it-with',
            productVariantId: this.variantId,
            cartData: json,
          });
        }
        if (cart && typeof cart.renderContents === 'function') {
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.opener || document.body);
          // close first: the drawer re-locks body scroll when it opens
          this.close({ restoreFocus: false });
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(json);
        } else {
          this.close({ restoreFocus: false });
          window.location = window.routes?.cart_url || '/cart';
        }
      } catch (err) {
        console.error('Wear it with: add to cart failed', err);
        this.setError("Couldn't add to cart. Please try again.");
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    onClick(event) {
      if (event.target.closest('[data-wiw-qv-close]') || event.target === this.querySelector('[data-wiw-qv-overlay]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const size = event.target.closest('[data-wiw-size]');
      if (size && this.content?.contains(size)) {
        event.preventDefault();
        this.selectSize(size);
        return;
      }
      const atc = event.target.closest('[data-wiw-atc]');
      if (atc && this.content?.contains(atc)) {
        event.preventDefault();
        this.addToCart(atc);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      // roving focus across the size radios
      const size = event.target.closest?.('[data-wiw-size]');
      if (!size || !this.sizeButtons?.length) return;
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      const i = this.sizeButtons.indexOf(size);
      const next = this.sizeButtons[(i + step + this.sizeButtons.length) % this.sizeButtons.length];
      this.selectSize(next);
      next.focus();
    }
  }

  if (!customElements.get('wear-it-with-quick-view')) {
    customElements.define('wear-it-with-quick-view', WearItWithQuickView);
  }
  if (!customElements.get('wear-it-with')) {
    customElements.define('wear-it-with', WearItWith);
  }
})();
